/*
 * PreviewPhoto
 *
 * A component used to preview uploaded file from user
 */

import loadable from "@loadable/component";
import { HTMLAttributes, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "src/components-v2/Button";
import Col from "src/components-v2/Grid/Col";
import Row from "src/components-v2/Grid/Row";
import Shimmer from "src/components-v2/Shimmer";
import { ImgContainer } from "./PreviewPhoto.styled";

import { getFileExtension } from "src/helpers/file";

import LogoUploadCsv from "src/assets/images/transaction/icon-uploadcsv.png";
import { PropsWithDataQaid } from "src/types/componentProps";

/** Load the component on demand, because react-pdf bundle size is too big! */
const PreviewPDF = loadable(() => import("./PreviewPDF"));

export type PreviewPhotoProps = PropsWithDataQaid<{
  /**
   * set if file is pdf
   */
  isPdf?: boolean;

  /**
   * File name
   */
  fileName?: string;

  /**
   * File src
   */
  previewSrc?: string;

  /**
   * Function that used to remove the file
   */
  removeFile?: HTMLAttributes<HTMLButtonElement>["onClick"];

  disabled?: boolean;

  heightImg?: string;
}>;

const defaultProps = {
  isPdf: false,
  fileName: "",
  previewSrc: "",
  removeFile: () => {}
};

function PreviewPhoto(props: PreviewPhotoProps) {
  const { t } = useTranslation("translation");
  const { disabled, fileName, isPdf, previewSrc, removeFile, heightImg, "data-qaid": dataQaid } = props;

  const [isLoaded, setIsLoaded] = useState(false);
  const fileExtension = getFileExtension(fileName || previewSrc);

  return (
    <ImgContainer heightImg={heightImg}>
      <Row>
        <Col className="u-text-left" xs={12} md={6}>
          {isPdf || fileExtension === "pdf" ? (
            <Suspense fallback={null}>
              <PreviewPDF src={previewSrc} />
            </Suspense>
          ) : (
            <>
              {!isLoaded && !previewSrc ? (
                <Shimmer height={220} width={280} />
              ) : (
                <img
                  src={["jpg", "jpeg", "png"].includes(fileExtension?.toLocaleLowerCase()) ? previewSrc : LogoUploadCsv}
                  alt="user-id"
                  style={{ display: isLoaded ? "block" : "none" }}
                  onLoad={() => setIsLoaded(true)}
                  className="fs-exclude"
                />
              )}
            </>
          )}
        </Col>
        <Col className="u-text-right" xs={12} md={6}>
          {fileName && (
            <div className="u-font-bold u-mb-4 u-break-all">
              {fileName.length >= 65 ? `${fileName.slice(0, 65)}...${fileExtension}` : fileName}
            </div>
          )}
          <Button
            variant="secondary"
            onClick={removeFile}
            disabled={disabled}
            outline
            data-qaid={`${dataQaid}-reupload-button`}
          >
            {t("preview-photo.reupload")}
          </Button>
        </Col>
      </Row>
    </ImgContainer>
  );
}

PreviewPhoto.defaultProps = defaultProps;

export default PreviewPhoto;
